import { Plus, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ACCURATE_ROLES, ENDPOINTS } from '../../../helpers/constants';
import { getCompanyEmail, getTeamManagementOverviewColumns } from '../../../helpers/datasheetConstants';
import { onCreateApiRequestData, onUpdateApiRequestData, selectStyles } from '../../../helpers/global';
import AlertModal from '../../AlertModal';
import DataTable from '../../common/data-table/DataTable';
import UserFormModal from '../../Users/UserFormModal';

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' }
];

const TeamManagementOverviewTab = ({ teamMembers = [], isTeamMembersLoading = false, onRefreshTeamMembers }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState(STATUS_OPTIONS[1]);
  const [showAddEditUserModal, setShowAddEditUserModal] = useState(false);
  const [addEditUser, setAddEditUser] = useState(null);
  const [itemAddingUpdating, setItemAddingUpdating] = useState(false);
  const [statusChangeMeta, setStatusChangeMeta] = useState(null);
  const [userToReinvite, setUserToReinvite] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [reinviting, setReinviting] = useState(false);

  const columns = useMemo(
    () =>
      getTeamManagementOverviewColumns({
        setAddEditUser,
        setShowAddEditUserModal,
        setUserToReinvite,
        setStatusChangeMeta
      }),
    []
  );

  const filteredRows = useMemo(() => {
    let rows = [...teamMembers];
    const statusVal = status?.value;
    if (statusVal && statusVal !== 'all') {
      const want = statusVal === 'enabled' ? true : false;
      rows = rows.filter(r => r.isEnabled === want);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => {
        const searchString = [r.name, getCompanyEmail(r), r.role, r.departmentRoles]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchString.includes(q);
      });
    }
    return rows;
  }, [searchQuery, status, teamMembers]);

  const handleSubmitUser = async form => {
    setItemAddingUpdating(true);

    if (addEditUser?._id) {
      const { error } = await makeRESTApiRequest(
        onUpdateApiRequestData('Users', ENDPOINTS.USERS_UPDATE, { item: addEditUser }, form)
      );
      setItemAddingUpdating(false);
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('User updated successfully');
    } else {
      const { error } = await makeRESTApiRequest(onCreateApiRequestData('Users', '/users', { ...form }, null));
      setItemAddingUpdating(false);
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('User invited successfully');
    }

    setShowAddEditUserModal(false);
    setAddEditUser(null);
    onRefreshTeamMembers?.();
  };

  const handleUpdateUserStatus = async () => {
    if (!statusChangeMeta?.user) return;
    setStatusUpdateLoading(true);

    const { user: selectedUser, isEnabled } = statusChangeMeta;
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.USERS_UPDATE,
      method: 'POST',
      requestBody: {
        _id: selectedUser._id,
        isEnabled,
        email: getCompanyEmail(selectedUser) || selectedUser.email,
        organization: selectedUser?.organization?._id || selectedUser?.organization
      }
    });

    setStatusUpdateLoading(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success(`User ${isEnabled ? 'enabled' : 'disabled'} successfully`);
    setStatusChangeMeta(null);
    onRefreshTeamMembers?.();
  };

  const handleReinviteUser = async () => {
    if (!userToReinvite) return;
    setReinviting(true);

    const requestBody = {
      staff: [{ email: userToReinvite.email, name: userToReinvite.name }]
    };

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVITE_STAFF,
      method: 'POST',
      requestBody
    });
    setReinviting(false);
    if (error) {
      toast.error(error);
      return;
    }

    const invitedCount = response?.invited?.length || 0;
    if (invitedCount > 0) {
      toast.success(`Invitation sent to ${userToReinvite.name}`);
    } else {
      toast.error('Failed to send invitation');
    }
    setUserToReinvite(null);
  };

  const isEnabling = statusChangeMeta?.isEnabled === true;
  const statusAlertText = isEnabling
    ? 'Are you sure you want to enable this team member?'
    : 'Disable this team member? Their record and employment history will be kept, and the company email will become available to assign to someone else.';

  return (
    <div className="mt-3">
      <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center" style={{ gap: 12 }}>
        <div className="position-relative flex-grow-1">
          <Search
            className="icon-md position-absolute"
            style={{
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: '#6c757d'
            }}
          />
          <Form.Control
            type="text"
            size="lg"
            className="bg-white search-input text-dark xsmallFont"
            placeholder="Search by name, email, role, or department..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 42, border: '1px solid #e5e7ea', fontWeight: 'normal' }}
          />
        </div>
        <div className="flex-shrink-0">
          <Select
            classNamePrefix="select"
            className="basic-single select-organization"
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            styles={selectStyles}
          />
        </div>
        <Button
          variant="info"
          className="d-flex align-items-center"
          onClick={() => {
            setAddEditUser(null);
            setShowAddEditUserModal(true);
          }}
          style={{ minHeight: '40px' }}
        >
          <Plus size={16} className="mr-1" />
          Add Team Member
        </Button>
      </div>

      <div className="mt-3" style={{ borderRadius: 12 }}>
        <DataTable
          data={filteredRows}
          columns={columns}
          rowKey="_id"
          sortable
          pagination={false}
          striped={false}
          hover
          bordered={false}
          responsive={false}
          maxHeight="min(65vh, 600px)"
          emptyMessage="No team members found"
          loading={isTeamMembersLoading}
        />
      </div>
      <UserFormModal
        show={showAddEditUserModal}
        user={addEditUser}
        roles={ACCURATE_ROLES}
        accurateTeamManagement
        teamMembers={teamMembers}
        isTeamMembersLoading={isTeamMembersLoading}
        onHide={() => {
          setShowAddEditUserModal(false);
          setAddEditUser(null);
        }}
        onSubmit={handleSubmitUser}
        showProgress={itemAddingUpdating}
      />
      <AlertModal
        show={statusChangeMeta !== null}
        onHide={() => setStatusChangeMeta(null)}
        onDismissClick={() => setStatusChangeMeta(null)}
        onContinueClick={handleUpdateUserStatus}
        showProgress={statusUpdateLoading}
        isEnabled={statusChangeMeta?.isEnabled}
        alertText={statusAlertText}
        progressText={`${isEnabling ? 'Enabling...' : 'Disabling...'}`}
      />
      <AlertModal
        show={userToReinvite !== null}
        onHide={() => !reinviting && setUserToReinvite(null)}
        onDismissClick={() => !reinviting && setUserToReinvite(null)}
        onContinueClick={handleReinviteUser}
        dismissButtonText="Cancel"
        continueButtonText="Send Invitation"
        continueButtonVariant="primary"
        showProgress={reinviting}
        progressText="Sending invitation..."
        alertText={
          userToReinvite
            ? `Do you want to send another invitation link to ${userToReinvite.name}?`
            : 'Do you want to send another invitation link?'
        }
      />
    </div>
  );
};

export default TeamManagementOverviewTab;
